/**
 * Asset Location Picker.
 *
 * Inline location editor for the asset detail page. Always provides a
 * free-text input for manual location entry.  When storage areas are
 * configured, shows a structured picker below the text input that lets
 * users drill into business location → storage area → specific location.
 *
 * Double-click to activate, enter text or select from the tree.
 */

import { useState, useMemo } from "react";
import { MapPin, ChevronRight, Type, Layers } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { useBusinessLocations } from "@/features/locations/api";
import { toast } from "@/stores/toastStore";
import LocationComboSelect from "@/components/ui/LocationComboSelect";

interface StorageAreaItem {
  id: string;
  label: string;
  section_type: string;
  capacity: number | null;
  asset_count: number;
  location_scheme_id: string | null;
  location_count: number;
  business_location_id: string | null;
  is_placed: boolean;
}

interface AssetLocationPickerProps {
  assetId: string;
  currentLocation: string | null | undefined;
  currentYardSectionId: string | null | undefined;
  currentYardLocationId: string | null | undefined;
  onLocationChanged: () => void;
}

function AssetLocationPicker({
  assetId,
  currentLocation,
  currentYardSectionId,
  onLocationChanged,
}: AssetLocationPickerProps) {
  const [editing, setEditing] = useState(false);
  const [freeText, setFreeText] = useState(currentLocation || "");
  const [mode, setMode] = useState<"text" | "structured">("text");
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(
    null,
  );
  const qc = useQueryClient();

  const { data: locations } = useBusinessLocations();

  const { data: storageAreas } = useQuery<StorageAreaItem[]>({
    queryKey: ["storage-areas"],
    queryFn: async () =>
      (await apiClient.get("/api/v1/storage-areas")).data as StorageAreaItem[],
    enabled: editing,
  });

  const assignMutation = useMutation({
    mutationFn: async (data: {
      yard_section_id: string | null;
      yard_location_id?: string | null;
    }) =>
      (
        await apiClient.post("/api/v1/yards/assign-asset", {
          asset_id: assetId,
          ...data,
        })
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["asset"] });
      qc.invalidateQueries({ queryKey: ["yard-map"] });
      qc.invalidateQueries({ queryKey: ["storage-areas"] });
      onLocationChanged();
      setEditing(false);
      toast.success("Location updated");
    },
    onError: () => toast.error("Failed to update location"),
  });

  const freeTextMutation = useMutation({
    mutationFn: async (location: string) =>
      (await apiClient.patch(`/api/v1/assets/${assetId}`, { location })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["asset"] });
      onLocationChanged();
      setEditing(false);
      toast.success("Location updated");
    },
    onError: () => toast.error("Failed to update location"),
  });

  // Group storage areas by business location.
  const areasByLocation = useMemo(() => {
    if (!storageAreas) return new Map<string | null, StorageAreaItem[]>();
    const map = new Map<string | null, StorageAreaItem[]>();
    for (const a of storageAreas) {
      const key = a.business_location_id;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(a);
    }
    return map;
  }, [storageAreas]);

  const hasAreas = storageAreas && storageAreas.length > 0;

  if (!editing) {
    return (
      <span
        onDoubleClick={() => {
          setFreeText(currentLocation || "");
          setMode("text");
          setSelectedLocationId(null);
          setEditing(true);
        }}
        className={`cursor-pointer rounded-[var(--radius-sm)] px-1 -mx-1 transition-colors hover:bg-[var(--surface-sunken)] ${
          currentLocation
            ? "text-[var(--text-primary)]"
            : "text-[var(--text-tertiary)] italic"
        }`}
        title="Double-click to change location"
      >
        {currentLocation || "Set location"}
      </span>
    );
  }

  // --- Structured mode: browsing storage areas ---
  if (mode === "structured" && !selectedLocationId) {
    return (
      <div className="space-y-1">
        <div className="rounded-[var(--radius-md)] border border-[var(--color-interactive-primary)]/30 bg-[var(--surface-base)] shadow-sm max-h-64 overflow-y-auto">
          {/* Back to text input */}
          <button
            onClick={() => setMode("text")}
            className="w-full text-left px-3 py-1.5 text-[10px] text-[var(--color-interactive-primary)] hover:bg-[var(--surface-sunken)] border-b border-[var(--border-subtle)] flex items-center gap-1"
          >
            <Type className="h-3 w-3" /> Type location manually
          </button>

          {/* Unassign option */}
          {currentYardSectionId && (
            <button
              onClick={() => assignMutation.mutate({ yard_section_id: null })}
              className="w-full text-left px-3 py-2 text-xs text-[var(--text-danger)] hover:bg-[var(--fill-danger)] border-b border-[var(--border-subtle)]"
            >
              Clear location assignment
            </button>
          )}

          {hasAreas ? (
            Array.from(areasByLocation.entries()).map(([locId, areas]) => {
              const loc = locations?.find((l) => l.id === locId);
              return (
                <div key={locId || "unlinked"}>
                  <p className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)] bg-[var(--surface-sunken)]/50">
                    {loc?.name || "Unlinked Areas"}
                  </p>
                  {areas.map((area) => (
                    <button
                      key={area.id}
                      onClick={() => {
                        if (
                          area.location_scheme_id &&
                          area.location_count > 0
                        ) {
                          setSelectedLocationId(area.id);
                        } else {
                          assignMutation.mutate({ yard_section_id: area.id });
                        }
                      }}
                      className="w-full flex items-center justify-between px-3 py-2 text-xs hover:bg-[var(--surface-sunken)] text-[var(--text-primary)]"
                    >
                      <div className="flex items-center gap-2">
                        <MapPin className="h-3 w-3 text-[var(--text-tertiary)]" />
                        <span className="font-medium">{area.label}</span>
                        <span className="text-[var(--text-tertiary)] capitalize">
                          {area.section_type}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-[var(--text-tertiary)] tabular-nums">
                          {area.asset_count}
                          {area.capacity ? ` / ${area.capacity}` : ""} assets
                        </span>
                        {area.location_count > 0 && (
                          <ChevronRight className="h-3 w-3 text-[var(--text-tertiary)]" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              );
            })
          ) : (
            <p className="px-3 py-3 text-xs text-[var(--text-tertiary)] text-center">
              No storage areas configured yet.
            </p>
          )}
        </div>
        <button
          onClick={() => setEditing(false)}
          className="text-[10px] text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]"
        >
          Cancel
        </button>
      </div>
    );
  }

  // --- Structured mode: picking a specific location within an area ---
  if (mode === "structured" && selectedLocationId) {
    return (
      <LocationPicker
        areaId={selectedLocationId}
        onBack={() => setSelectedLocationId(null)}
        onCancel={() => {
          setSelectedLocationId(null);
          setEditing(false);
        }}
        onAssign={(sectionId, locationId) =>
          assignMutation.mutate({
            yard_section_id: sectionId,
            yard_location_id: locationId,
          })
        }
      />
    );
  }

  // --- Default: free-text input mode ---
  return (
    <div className="space-y-1.5">
      <div className="flex gap-1.5 items-end">
        <LocationComboSelect
          value={freeText}
          onChange={setFreeText}
          placeholder="Search or type a location..."
          className="flex-1"
        />
        <button
          onClick={() => freeTextMutation.mutate(freeText.trim())}
          disabled={freeTextMutation.isPending}
          className="h-[38px] px-2 rounded-[var(--radius-sm)] bg-[var(--color-interactive-primary)] text-white text-[10px] font-medium hover:opacity-90 disabled:opacity-50"
        >
          Save
        </button>
      </div>
      {hasAreas && (
        <button
          onClick={() => setMode("structured")}
          className="flex items-center gap-1 text-[10px] text-[var(--color-interactive-primary)] hover:underline"
        >
          <Layers className="h-3 w-3" /> Pick from storage areas
        </button>
      )}
      <button
        onClick={() => setEditing(false)}
        className="text-[10px] text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]"
      >
        Cancel
      </button>
    </div>
  );
}

/**
 * Sub-component for picking a specific location within a storage area.
 */
function LocationPicker({
  areaId,
  onBack,
  onCancel,
  onAssign,
}: {
  areaId: string;
  onBack: () => void;
  onCancel: () => void;
  onAssign: (sectionId: string, locationId: string) => void;
}) {
  const { data: locationsData } = useQuery<
    Array<{
      id: string;
      label: string;
      capacity: number;
      is_active: boolean;
      asset_count: number;
    }>
  >({
    queryKey: ["storage-area-locations", areaId],
    queryFn: async () => {
      const res = await apiClient.get(
        `/api/v1/storage-areas/${areaId}/locations`,
      );
      return res.data;
    },
  });

  return (
    <div className="space-y-1">
      <div className="rounded-[var(--radius-md)] border border-[var(--color-interactive-primary)]/30 bg-[var(--surface-base)] shadow-sm max-h-64 overflow-y-auto">
        <button
          onClick={onBack}
          className="w-full text-left px-3 py-1.5 text-[10px] text-[var(--color-interactive-primary)] hover:bg-[var(--surface-sunken)] border-b border-[var(--border-subtle)]"
        >
          &larr; Back to storage areas
        </button>
        {locationsData && locationsData.length > 0 ? (
          locationsData
            .filter((l) => l.is_active)
            .map((loc) => (
              <button
                key={loc.id}
                onClick={() => onAssign(areaId, loc.id)}
                className="w-full flex items-center justify-between px-3 py-2 text-xs hover:bg-[var(--surface-sunken)] text-[var(--text-primary)]"
              >
                <span className="font-medium">{loc.label}</span>
                <span className="text-[10px] text-[var(--text-tertiary)] tabular-nums">
                  {loc.asset_count} / {loc.capacity}
                </span>
              </button>
            ))
        ) : (
          <p className="px-3 py-3 text-xs text-[var(--text-tertiary)] text-center">
            No locations in this area.
          </p>
        )}
      </div>
      <button
        onClick={onCancel}
        className="text-[10px] text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]"
      >
        Cancel
      </button>
    </div>
  );
}

export default AssetLocationPicker;
